import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import startWith from 'lodash/startsWith';

import { getErrorMessage, useMutationCompletionHandler } from '@ts-core/lib/apollo';

import { UploadProgressState } from 'core/components/UploadProgressIndicator';
import { useFileUploader } from 'core/components/FileUploader';

import { createMediaItemMutation } from '../graphql/mutations';
import { getMediaItemsBySubjectQuery } from '../graphql/queries';

export default function useMediaUploader({ courseId, subjectId }) {
  const apolloClient = useApolloClient();
  const { addItemToCache } = useMutationCompletionHandler();

  const getFileType = useCallback((file) => {
    return startWith(file.type, 'audio') ? 'audios' : 'videos';
  }, []);

  const createVideo = useCallback(
    async ({ input, setState, setError }) => {
      setState(UploadProgressState.saving);
      try {
        await apolloClient.mutate({
          mutation: createMediaItemMutation,
          variables: {
            courseId,
            subjectId,
            input,
          },
          update: addItemToCache({
            query: getMediaItemsBySubjectQuery,
            queryField: 'mediaItems',
            variables: { courseId, subjectId },
            mutationField: 'mediaItem',
          }),
        });

        setState(UploadProgressState.success);

        setTimeout(() => {
          setState(UploadProgressState.idle);
        }, 3000);
      } catch (mutationError) {
        setState(UploadProgressState.failed);
        setError(getErrorMessage({ error: mutationError }));
      }
    },
    [addItemToCache, apolloClient, courseId, subjectId]
  );

  const { error, state, fileName, progress, onFileSelected, onClose } = useFileUploader({
    getFileType,
    onFileUploaded: createVideo,
  });

  return {
    fileName,
    error,
    progress,
    state,
    accept: 'audio/*, video/*',

    onFileSelected,
    onClose,
  };
}
